import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { FlowmetersService } from 'src/app/providers/services/flowmeters.service';
import { GatewaysService } from 'src/app/providers/services/gateways.service';
import { LoaderService } from 'src/app/providers/services/loader.service';
import { MaterialsService } from 'src/app/providers/services/materials.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { PumpsService } from 'src/app/providers/services/pumps.service';
import { SkidsService } from 'src/app/providers/services/skids.service';
import { AnalyzerMission } from 'src/app/storage/models/analyzer-mission.model';
import { Mission } from 'src/app/storage/models/mission.model';
import { SkidMission } from 'src/app/storage/models/skid-mission.model';
import { Skid } from 'src/app/storage/models/skid.model';
import { AnalyzerSelection } from '../../common/add-analyzer/add-analyzer.component';

@Component({
    selector: 'app-material-config',
    templateUrl: './material-config.component.html',
    styleUrls: ['./material-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialConfigComponent implements OnInit, OnDestroy {

  mission: Mission;

  missionSkids: SkidMission[] = [];
  addingSkid = false;
  missionSkidToUpdate: SkidMission;

  missionAnalyzer: AnalyzerMission;
  addingAnalyzer = false;

  validatingMaterial = false;

  private missionSubscription: Subscription;
  private skidsSubscription: Subscription;
  private analyzerSubscription: Subscription;

  constructor(
    private missionsService: MissionsService,
    private skidsService: SkidsService,
    private analyzersServices: AnalyzersService,
    private navigationService: NavigationService,
    private pumpsService: PumpsService,
    private gatewaysService: GatewaysService,
    private flowmetersService: FlowmetersService,
    private loader: LoaderService,
    private materialsService: MaterialsService
  ) { }

  ngOnInit(): void {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      await this.init(mission);

      // Get current mission skids & analyzer
      if (this.skidsSubscription) { this.skidsSubscription.unsubscribe(); }
      this.skidsSubscription = this.skidsService.missionSkids$.subscribe(async (missionSkids: SkidMission[]) => {
        if (missionSkids != null) {
          this.missionSkids = missionSkids;

          // Set equipments composing the skids and mission skids
          this.materialsService.setSkidsEquipments(this.skidsService.skids$.getValue());
          this.materialsService.setMissionSkidsEquipments(this.missionSkids);
        } else {
          await this.backToMissionConfig();
        }
      });

      if(this.analyzerSubscription) { this.analyzerSubscription.unsubscribe(); }
      this.analyzerSubscription = this.analyzersServices.missionAnalyzers$.subscribe(async (missionAnalyzers: AnalyzerMission[]) => {
        if (missionAnalyzers.length > 0) {
          this.materialsService.setMissionAnalyzersEquipments(missionAnalyzers);
          this.missionAnalyzer = missionAnalyzers[0];
        } else {
          this.missionAnalyzer = null;
        }
      });
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
    if (this.skidsSubscription) { this.skidsSubscription.unsubscribe(); }
    if (this.analyzerSubscription) { this.analyzerSubscription.unsubscribe(); }
  }

  async init(mission: Mission): Promise<void> {
    this.mission = mission;

    this.loader.show();

    // Set global skids, navigate back to mission configuration if couldn't be fetched
    const skids = await this.skidsService.getSkids(mission.marketname);
    const pumps = await this.pumpsService.getPumps(mission.marketname, mission.missionId);
    const gateways = await this.gatewaysService.getGateways(mission.marketname, mission.missionId);
    const flowmeters = await this.flowmetersService.getFlowmeters(mission.marketname, mission.missionId);
    const analyzers = await this.analyzersServices.getAnalyzers(mission.marketname, mission.missionId);

    this.loader.hide();

    if (skids == null || pumps == null || gateways == null || flowmeters == null || analyzers == null) {
      await this.backToMissionConfig(); 
    } else {
      this.skidsService.skids$.next(skids);
      this.pumpsService.pumps$.next(pumps);
      this.gatewaysService.gateways$.next(gateways);
      this.flowmetersService.flowmeters$.next(flowmeters);
      this.analyzersServices.analyzers$.next(analyzers);
    }
  }

  async backToMissionConfig() {
    await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_CONFIG);
  }

  openAddSkidModal(missionSkid?: SkidMission): void {
    this.missionSkidToUpdate = !!missionSkid ? missionSkid : null;
    this.addingSkid = true;
  }

  async closeAddSkidModal(skid?: Skid): Promise<void> {
    this.addingSkid = false;

    // Add/update skid if it is provided
    if (skid) {
      const missionSkid = new SkidMission({
        skidId: skid.skidId,
        pumpId: null,
        gatewayId: null,
        flowmeterId: null,
        fieldprotocolName: skid.fieldprotocolName,
        changedBy: null,
        changedOn: Math.floor(Date.now() / 1000).toString(),
        createdBy: null,
        createdOn: null
      });

      this.materialsService.setMissionSkidsEquipments([missionSkid]);
      
      const isMissionSkidUpdated = await this.skidsService.addUpdateMissionSkid(this.mission.missionId, missionSkid);
      if (isMissionSkidUpdated) {
        const missionSkids = this.skidsService.missionSkids$.getValue().filter(skid => skid.skidId !== missionSkid.skidId);
        missionSkids.push(missionSkid);
        this.skidsService.missionSkids$.next(missionSkids);

        if (this.mission.materialstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
          // Set material status to 'IN PROGRESS'
          await this.setMaterialStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
        }
      }
    }
  }

  async removeMissionSkid(missionSkid: SkidMission): Promise<void> {
    const isMissionSkidRemoved = await this.skidsService.removeMissionSkid(this.mission.missionId, missionSkid.skidId);
    if (isMissionSkidRemoved) {
      const missionSkids = this.skidsService.missionSkids$.getValue().filter(skid => skid.skidId !== missionSkid.skidId);
      this.skidsService.missionSkids$.next(missionSkids);
    }
  }

  openAnalyzerModal(): void {
    this.addingAnalyzer = true;
  }

  async closeAnalyzerModal(selection?: AnalyzerSelection): Promise<void> {
    this.addingAnalyzer = false;

    const analyzer = selection?.analyzer;

    // Add/update analyzer if it is provided
    if (analyzer) {
      const missionAnalyzer = new AnalyzerMission({
        analyzerpk: analyzer.analyzerpk, 
        analyzerId: analyzer.analyzerId,
        gatewayId: analyzer.telemetrysuppliername === CONSTANTS.TELEMETRY_SUPPLIER.ARKEMA ? analyzer.gatewayId : null,
        fieldprotocolName: analyzer.fieldprotocolName,
        handleFlyingH2: selection.handleFlyingH2 === true,
        changedBy: null,
        changedOn: Math.floor(Date.now() / 1000).toString(),
        createdBy: null,
        createdOn: null
      });

      this.materialsService.setMissionAnalyzersEquipments([missionAnalyzer]);

      const isAnalyzerUpdated = await this.analyzersServices.addUpdateMissionAnalyzer(this.mission.missionId, missionAnalyzer);
      if (isAnalyzerUpdated) {
        this.analyzersServices.missionAnalyzers$.next([missionAnalyzer]);
      }

      if (this.mission.materialstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
        // Set material status to 'IN PROGRESS'
        await this.setMaterialStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
      }
    }
  }

  async removeAnalyzer(): Promise<void> {
    const isAnalyzerRemoved = await this.analyzersServices.removeMissionAnalyzer(this.mission.missionId, this.missionAnalyzer.analyzerId);
    if (isAnalyzerRemoved) { this.analyzersServices.missionAnalyzers$.next([]); }
  }

  async setMaterialStatus(status: number): Promise<void> {
    // Update material status
    const isStatusUpdated = await this.missionsService.updateMissionMaterialStatus(this.mission.missionId, status);

    // If update succeeded, set mission material status
    if (isStatusUpdated) {
      this.mission.materialstatus = status;
    }
  }

  isMaterialComplete(): boolean {
    return this.mission.materialstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;
  }

  getMaterialStatusIcon(): string {
    const icon = this.isMaterialComplete() ? 'edit-icon' : 'complete-icon';
    return `assets/images/${icon}.svg`;
  }

  async toggleMaterialStatus(): Promise<void> {
    const status = (this.mission.materialstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED) ?
                    CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS :
                    CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;

    

    // Validate material for 'COMPLETED' status
    if (status === CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED ) {
      this.validatingMaterial = true;
    } else {
      await this.setMaterialStatus(status);
    }
  }

  closeMaterialValidation(): void {
    this.validatingMaterial = false;
  }
}
